import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { commentQueryKeys, reviewQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import { getUserAliasReviewsRatings } from 'services/alias';
import {
  getUserArticleCommentsRatings,
  getUserOfferCommentsRatings,
  getUserThreadCommentsRatings,
  postCommentDislike,
  postCommentLike,
} from 'services/comment';

import useQueryClient from './useQueryClient';

import ALIAS from 'constants/alias';
import BLOG from 'constants/blog';
import COMMENT from 'constants/comment';
import FORUM from 'constants/forum';
import OFFER from 'constants/offer';

/**
 * Despite `alias` not really being a Promobit domain, we had use-cases that are
 * very similar to the real domains, like reviews, so we reuse part of
 * the existing structure to simplify our code.
 */
const DATA_BY_DOMAIN = {
  [ALIAS.DOMAIN]: {
    cacheKey: 'aliasId',
    domainQueryKeys: reviewQueryKeys,
    fetcher: async (aliasId, opts) =>
      // Padronize the stored data shape
      (await getUserAliasReviewsRatings({ aliasId }, opts))?.data,
  },
  [OFFER.DOMAIN]: {
    cacheKey: 'offerId',
    domainQueryKeys: commentQueryKeys.offer,
    fetcher: (offerId, opts) => getUserOfferCommentsRatings({ offerId }, opts),
  },
  [BLOG.ARTICLES.DOMAIN]: {
    cacheKey: 'articleId',
    domainQueryKeys: commentQueryKeys.article,
    fetcher: (articleId, opts) =>
      getUserArticleCommentsRatings({ articleId }, opts),
  },
  [FORUM.THREAD.DOMAIN]: {
    cacheKey: 'threadId',
    domainQueryKeys: commentQueryKeys.thread,
    fetcher: (threadId, opts) =>
      getUserThreadCommentsRatings({ threadId }, opts),
  },
};

const useRatings = ({ domain, domainId, onLike, onDislike }) => {
  const queryClient = useQueryClient();
  const { isLoggedIn, runCallbackIfLoggedIn } = useUser();

  const { cacheKey, fetcher, domainQueryKeys } = DATA_BY_DOMAIN[domain];

  const { data: ratings } = useQuery({
    queryKey: domainQueryKeys.ratingsList({ [cacheKey]: domainId }),
    queryFn: ({ signal }) => fetcher(domainId, { signal }),
    enabled: isLoggedIn,
  });

  const { mutate: createLike } = useMutation({
    mutationFn: (id) => postCommentLike({ commentId: id }),
    onSuccess: ([{ action }], id) => {
      if (onLike) {
        onLike(id, action);
      }

      if (action === COMMENT.ACTIONS.LIKE) {
        queryClient.setQueryData(
          domainQueryKeys.ratingsList({ [cacheKey]: domainId }),
          {
            dislikes: ratings
              ? ratings.dislikes.filter((dislikedId) => dislikedId !== id)
              : [],
            likes: [...ratings.likes, id],
          }
        );
        return;
      }

      queryClient.setQueryData(
        domainQueryKeys.ratingsList({ [cacheKey]: domainId }),
        {
          ...ratings,
          likes: ratings
            ? ratings.likes.filter((likedId) => likedId !== id)
            : [],
        }
      );
    },
  });

  const { mutate: createDislike } = useMutation({
    mutationFn: (id) => postCommentDislike({ commentId: id }),
    onSuccess: ([{ action }], id) => {
      if (onDislike) {
        onDislike(id, action);
      }

      if (action === COMMENT.ACTIONS.DISLIKE) {
        queryClient.setQueryData(
          domainQueryKeys.ratingsList({ [cacheKey]: domainId }),
          {
            likes: ratings.likes.filter((likedId) => likedId !== id),
            dislikes: [...ratings.dislikes, id],
          }
        );
        return;
      }

      queryClient.setQueryData(
        domainQueryKeys.ratingsList({ [cacheKey]: domainId }),
        {
          ...ratings,
          dislikes: ratings.dislikes.filter((dislikedId) => dislikedId !== id),
        }
      );
    },
  });

  const addLike = useCallback(
    (id) => runCallbackIfLoggedIn(createLike, id),
    [runCallbackIfLoggedIn]
  );

  const addDislike = useCallback(
    (id) => runCallbackIfLoggedIn(createDislike, id),
    [runCallbackIfLoggedIn]
  );

  const isLiked = (id) => !!ratings?.likes.some((likedId) => likedId === id);

  const isDisliked = (id) =>
    !!ratings?.dislikes.some((dislikedId) => dislikedId === id);

  return {
    ratings,
    addDislike,
    addLike,
    isDisliked,
    isLiked,
  };
};

export default useRatings;
